/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { homeAction } from "@redux/modules";
import { API } from "@services/http.service";

// Yarışma
import { getMyCompetitions } from "@pages/private/Competition/competition.api";
import {
  CORNER_TEMPLATE_TITLES,
  timeWithSuffix,
} from "@pages/private/Competition/competition.constants";
import { themeStyle } from "@pages/private/Competition/competition.theme";
import CornerView from "@pages/private/Competition/CornerView";

// Hooks
import useRoleAccess from "@hooks/useRoleAccess";

// Anket (eski ana sayfa tasarımıyla board içinde)
import HomeSurvey from "../HomeSurvey";

// Gömülü ikonlar. Oyunlarda tanımdaki "Simge" görseli, ankette admin'in yüklediği board ikonu
// varsa o kullanılır; yoksa bunlar.
import CornerRamadanIcon from "@assets/images/activity-board/corner-ramadan.svg";
import SurveyIcon from "@assets/images/activity-board/survey.svg";
import SurpriseQuestionIcon from "@assets/images/activity-board/surprise-question.svg";
import { GAME_ICONS } from "../gameIcons";

// Styles
import "./styles.scss";

/**
 * Oyun servisinin döndüğü anahtarlar → ana sayfa / menüdeki oyun bilgileri. componentName ve
 * path, Oyunlar bölümüyle aynı yetki kontrolleri için kullanılır (bkz. HomeGames).
 */
const GAMES = {
  WORD_PUZZLES: {
    componentName: "KELİME AVI",
    path: "/games/word-puzzles",
    title: "Kelime Bulmaca",
    text: "Gizli kelimeyi bul, puanı kap!",
    tone: "amber",
  },
  LUCKY_WHEEL: {
    componentName: "ŞANS ÇARKI",
    path: "/games/question-wheel",
    title: "Şans Çarkı",
    text: "Çarkı çevir, şansını dene!",
    tone: "red",
  },
  FIND_MATE: {
    componentName: "EŞLE KAZAN",
    path: "/games/find-mate",
    title: "Eşini Bul",
    text: "Kartların eşini bul, puan kazan!",
    tone: "blue",
  },
  GUESS_PICTURE: {
    componentName: "RESMİ TAHMİN ET",
    path: "/games/guess-picture",
    title: "Resmi Tahmin Et",
    text: "Kutuları aç, resmi tahmin et, hediye kazanma şansı yakala!",
    tone: "blue",
  },
  SCORE_HUNT: {
    componentName: "PUAN AVI",
    path: "/games/score-hunt",
    title: "Puan Avı",
    text: "Puan Avı sembollü ürünü bul, soruya ulaş!",
    tone: "orange",
  },
  WHO_WANTS_SCORE: {
    componentName: "KİM 1000 PUAN İSTER",
    path: "/games/who-wants-score",
    title: "Kim 1000 Puan İster",
    text: "Soruları bil, 1000 puana ulaş!",
    tone: "pink",
  },
  BEST_PLATE: {
    componentName: "EN İYİ TABAK",
    path: "/games/best-plate",
    title: "En İyi Tabak",
    text: "En iyi tabağını yükle, oyla ve haftanın birincisi ol!",
    tone: "gold",
  },
};

/** Köşe şablonunun gömülü ikonu; yeni şablon eklenince ikonu da buraya eklenir. */
const CORNER_ICONS = {
  RAMADAN: CornerRamadanIcon,
};

const BOARD_GAMES_URL = "/game/activityBoard/games";

/**
 * Sürpriz Soru (Saatlik / Sürpriz moddaki yarışma) bugün katılınabilir mi: bugünün sorusu açık ya
 * da birazdan açılacak ve kazanma hakkı sürüyor. Yanıtlayınca / hakkı bitince o gün görünmez.
 */
const canPlaySurprise = (competition) =>
  competition.mode === "HOURLY" &&
  ["OPEN", "NOT_STARTED"].includes(competition.todayState) &&
  (competition.winCount || 0) < (competition.maxWinCount || 1);

const surpriseStatus = (competition) =>
  competition.todayState === "NOT_STARTED"
    ? `Sürpriz soru bugün saat ${timeWithSuffix(competition.todayOpensAt)} açılıyor.`
    : "Sürpriz soru seni bekliyor!";

/**
 * Etkinlik ikonları şeridi. Sayfaya sığmadığında yatay kayar; kaydırılabilecek tarafta yarı saydam
 * ok butonu görünür (özellikle mobilde sağda başka etkinlik olduğu anlaşılsın diye).
 */
const IconStrip = ({ items, selectedKey, onSelect }) => {
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    // Alt piksel yuvarlamaları için 2px pay
    setCanLeft(track.scrollLeft > 2);
    setCanRight(track.scrollLeft + track.clientWidth < track.scrollWidth - 2);
  }, []);

  useEffect(() => {
    update();
    const track = trackRef.current;
    if (!track) return undefined;
    track.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // İkon görselleri yüklenince genişlik değişebilir
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    observer?.observe(track);
    Array.from(track.children).forEach((child) => observer?.observe(child));
    return () => {
      track.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [items.length, update]);

  const scrollBy = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({
      left: direction * Math.max(track.clientWidth * 0.7, 120),
      behavior: "smooth",
    });
  };

  return (
    <div className="activity-board-strip">
      {canLeft && (
        <button
          type="button"
          className="activity-board-arrow is-left"
          aria-label="Önceki etkinlikler"
          onClick={() => scrollBy(-1)}
        >
          <FaChevronLeft />
        </button>
      )}
      <div className="activity-board-icons" role="tablist" ref={trackRef}>
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={item.key === selectedKey}
            className={`activity-board-icon${
              item.key === selectedKey ? " is-selected" : ""
            }`}
            onClick={() => onSelect(item.key)}
          >
            <img src={item.icon} alt="" />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
      {canRight && (
        <button
          type="button"
          className="activity-board-arrow is-right"
          aria-label="Sonraki etkinlikler"
          onClick={() => scrollBy(1)}
        >
          <FaChevronRight />
        </button>
      )}
    </div>
  );
};

/**
 * Ana sayfa Aktivite Board'u: kullanıcının ŞU AN katılabileceği etkinlikler otomatik listelenir;
 * admin buraya kayıt eklemez.
 * - Sürpriz Soru: Saatlik / Sürpriz moddaki yarışma, bugün katılabiliyorsa; tıklanınca yarışmaya gider.
 * - Yarışma Köşesi: hedef kitlesindeki yarışmanın köşesi açıksa, katılma hakkı olmasa da görünür.
 *   Diğer yarışmalar board'da ayrıca yer almaz; köşe yarışmaya bağlanır (duyuru slider'ı ve
 *   Yarışmalar menüsü ayrıca vardır).
 * - Anket: aktif anket board'da (eski ana sayfa tasarımıyla) cevaplanır; cevaplandıktan sonra
 *   sonuçlarıyla kalmaya devam eder.
 * - Oyunlar: oynamadığı (ya da yarıda bıraktığı) aktif oyunlar; oynadıktan sonra görünmez.
 * Tek etkinlik varsa yalnızca onun alanı, birden fazlaysa üstte ikonlar ve seçilenin alanı görünür.
 */
const HomeActivityBoard = ({ tenantPaths = [] }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const surveyList = useSelector((store) => store.home.surveyList);
  const { activeComponents, isComponentActive, isCategoryActive } =
    useRoleAccess();

  const [competitions, setCompetitions] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  const componentsReady = activeComponents?.length > 0;
  const showCompetitions =
    componentsReady &&
    isComponentActive("YARIŞMALAR") &&
    !tenantPaths.includes("/competitions");
  const showSurvey = componentsReady && isComponentActive("ANKET");
  const showGames = componentsReady && isCategoryActive("OYUNLAR");

  useEffect(() => {
    if (!showCompetitions) {
      setCompetitions([]);
      return undefined;
    }
    let active = true;
    getMyCompetitions()
      .then((res) => {
        if (active) setCompetitions(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [showCompetitions]);

  useEffect(() => {
    if (showSurvey) dispatch(homeAction.getHomepageSurvey());
  }, [showSurvey]);

  useEffect(() => {
    if (!showGames) {
      setGames([]);
      return undefined;
    }
    let active = true;
    API.get(BOARD_GAMES_URL)
      .then((res) => {
        if (active) setGames(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [showGames]);

  // Ana sayfada (eskiden olduğu gibi) ilk aktif anket gösterilir.
  const survey = showSurvey
    ? surveyList?.content?.find((item) => item !== null)
    : null;

  const items = useMemo(() => {
    const list = [];
    competitions.filter(canPlaySurprise).forEach((competition) => {
      list.push({
        key: `surprise-${competition.id}`,
        kind: "SURPRISE",
        label: "Sürpriz Soru",
        icon: SurpriseQuestionIcon,
        tone: "orange",
        competition,
      });
    });
    competitions
      .filter((competition) => competition.corner)
      .forEach((competition) => {
        const template = competition.corner.template;
        list.push({
          key: `corner-${competition.id}`,
          kind: "CORNER",
          label: CORNER_TEMPLATE_TITLES[template] || "Yarışma Köşesi",
          icon: CORNER_ICONS[template] || CornerRamadanIcon,
          tone: "navy",
          competition,
        });
      });
    if (survey) {
      list.push({
        key: `survey-${survey.id}`,
        kind: "SURVEY",
        label: "Anket",
        icon: survey.boardIconUrl || SurveyIcon,
        tone: "teal",
        survey,
      });
    }
    games.forEach((game) => {
      const meta = GAMES[game.key];
      if (
        !meta ||
        !isComponentActive(meta.componentName) ||
        tenantPaths.includes(meta.path)
      ) {
        return;
      }
      list.push({
        key: `game-${game.key}`,
        kind: "GAME",
        label: meta.title,
        icon: game.iconUrl || GAME_ICONS[game.key],
        tone: meta.tone,
        game: { ...meta, continues: game.continues },
      });
    });
    return list;
  }, [competitions, survey, games, tenantPaths, isComponentActive]);

  const selected =
    items.find((item) => item.key === selectedKey) || items[0] || null;

  if (!selected) return null;

  const renderPanel = (item) => {
    if (item.kind === "CORNER") {
      const { competition } = item;
      return (
        <div className="activity-board-corner" style={themeStyle(competition)}>
          <CornerView
            competition={competition}
            onGoToCompetition={() =>
              navigate(`/competitions/${competition.id}`)
            }
          />
        </div>
      );
    }

    if (item.kind === "SURPRISE") {
      const { competition } = item;
      return (
        <div className={`activity-board-panel tone-${item.tone}`}>
          <div className="activity-board-panel-text">
            <h3>{competition.competitionTitle || competition.name}</h3>
            <p>
              {[competition.competitionSubTitle, surpriseStatus(competition)]
                .filter(Boolean)
                .join(" ")}
            </p>
            <Link
              to={`/competitions/${competition.id}`}
              className="activity-board-cta"
            >
              Sürpriz Soruya Git
            </Link>
          </div>
          <img className="activity-board-panel-icon" src={item.icon} alt="" />
        </div>
      );
    }

    if (item.kind === "SURVEY") {
      return <HomeSurvey embedded />;
    }

    return (
      <div className={`activity-board-panel tone-${item.tone}`}>
        <div className="activity-board-panel-text">
          <h3>{item.game.title}</h3>
          <p>{item.game.text}</p>
          <Link to={item.game.path} className="activity-board-cta">
            {item.game.continues ? "Oyuna Devam Et" : "Hemen Oyna"}
          </Link>
        </div>
        <img className="activity-board-panel-icon" src={item.icon} alt="" />
      </div>
    );
  };

  return (
    <section className="activity-board-section pt-4 pb-4">
      <div className="container">
        {items.length > 1 && (
          <IconStrip
            items={items}
            selectedKey={selected.key}
            onSelect={setSelectedKey}
          />
        )}
        {renderPanel(selected)}
      </div>
    </section>
  );
};

export default HomeActivityBoard;
